import { asyncToGenerator as _asyncToGenerator, regeneratorRuntime as _regeneratorRuntime } from '../_virtual/_rollupPluginBabelHelpers.js';
import intersection from 'lodash/intersection';
import { loadApiData, loadExternalData } from './ApiUtil.js';
import _get from 'lodash/get';
/**
 * Returns true if any of the requiredBusinessLines are available
 *
 * @param {string|array} requiredBusinessLine - The business line required
 * @param {array} businessLines - The businessLines that the requestor has
 */

var hasBusinessLine = function hasBusinessLine(requiredBusinessLine, businessLines) {
  return hasAnyValues(requiredBusinessLine, businessLines);
};
/**
 * Returns true if the requiredCapability is available
 *
 * @param {string} requiredCapability - The capability required
 * @param {array} capabilities - The capabilities that the requestor has
 */


var hasCapability = function hasCapability(requiredCapability, capabilities) {
  return hasValue(requiredCapability, capabilities);
};
/**
 * Returns true if the required feature flag is enabled for the user
 *
 * @param {string} flag - The feature flag name
 * @param {string} adminifiUrl - The adminifi URL (needed if features are required from other app)
 * @param {string} authToken - The auth token (needed if features are required from other app)
 */


var hasFeatureEnabled = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee(flag, adminifiUrl, authToken) {
    var url, features, requiredFeature;
    return _regeneratorRuntime().wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            // Note: you can add feature flags using the Flipper dashboard
            // at '/admin/features/features' in adminifi
            url = adminifiUrl ? "".concat(adminifiUrl, "/api/v1/features.json") : "/api/data/features.json";
            features = null;

            if (!authToken) {
              _context.next = 8;
              break;
            }

            _context.next = 5;
            return loadExternalData(url, authToken);

          case 5:
            features = _context.sent;
            _context.next = 11;
            break;

          case 8:
            _context.next = 10;
            return loadApiData(url);

          case 10:
            features = _context.sent;

          case 11:
            requiredFeature = features && features.find(function (feature) {
              return feature.name === flag;
            });
            return _context.abrupt("return", requiredFeature && requiredFeature.enabled);

          case 13:
          case "end":
            return _context.stop();
        }
      }
    }, _callee);
  }));

  return function hasFeatureEnabled(_x, _x2, _x3) {
    return _ref.apply(this, arguments);
  };
}();
/**
 * Returns true if the required permission exists
 *
 * @param {string} requiredPermission - The permission required
 * @param {array} permissions - The permissions that the requestor has
 */


var hasPermission = function hasPermission(requiredPermission, permissions) {
  return hasValue(requiredPermission, permissions) || !!(permissions && permissions.includes('all_access'));
};
/**
 * Returns true if the required value exists
 *
 * @param {string} requiredValue - The value required
 * @param {array} values - The values that the requestor has
 */


var hasValue = function hasValue(requiredValue, values) {
  if (!requiredValue) {
    return true;
  }

  values = values || [];
  return values.includes(requiredValue);
};
/**
 * Returns true if the any of the required values exists
 *
 * @param {string|array} requiredValues - The values required
 * @param {array} values - The values that the requestor has
 */


var hasAnyValues = function hasAnyValues(requiredValues, values) {
  if (!requiredValues) {
    return true;
  }

  requiredValues = Array.isArray(requiredValues) ? requiredValues : [requiredValues];

  if (requiredValues.length === 0) {
    return true;
  }

  values = values || [];
  return intersection(requiredValues, values).length > 0;
};
/**
 * Returns true if all checks pass
 *
 * @param {object} props - The props that would be passed to AccessControlWrapper
 */


var hasAccess = function hasAccess(props) {
  return hasRequiredOrganizationType(props) && hasRequiredBusinessLine(props) && hasRequiredCapability(props) && hasRequiredPermission(props) && hasRequiredValue(props);
};

var hasRequiredOrganizationType = function hasRequiredOrganizationType(props) {
  var organizationType = props.organizationType,
      requiredOrganizationType = props.requiredOrganizationType;

  if (!requiredOrganizationType) {
    return true;
  }

  var organizationTypeFromOrg = getProp(props, 'organization.organization_type');

  if (!(organizationType || organizationTypeFromOrg)) {
    return false;
  }

  return requiredOrganizationType === (organizationType || organizationTypeFromOrg);
};

var hasRequiredBusinessLine = function hasRequiredBusinessLine(props) {
  var businessLines = props.businessLines,
      requiredBusinessLine = props.requiredBusinessLine;

  if (!requiredBusinessLine) {
    return true;
  }

  var userBusinessLines = getProp(props, 'user.business_lines');

  if (!(businessLines || userBusinessLines)) {
    return false;
  }

  return hasBusinessLine(requiredBusinessLine, businessLines || userBusinessLines);
};

var hasRequiredCapability = function hasRequiredCapability(props) {
  var capabilities = props.capabilities,
      requiredCapability = props.requiredCapability;

  if (!requiredCapability) {
    return true;
  }

  var capability_list = getProp(props, 'organization.capability_list');

  if (!(capabilities || capability_list)) {
    return false;
  }

  return hasCapability(requiredCapability, capabilities || capability_list);
};

var hasRequiredPermission = function hasRequiredPermission(props) {
  var permissions = props.permissions,
      requiredPermission = props.requiredPermission;

  if (!requiredPermission) {
    return true;
  }

  var adminPermissions = getProp(props, 'admin.permissions');

  if (!(permissions || adminPermissions)) {
    return false;
  }

  return hasPermission(requiredPermission, permissions || adminPermissions);
};

var hasRequiredValue = function hasRequiredValue(props) {
  var values = props.values,
      requiredValue = props.requiredValue;

  if (!requiredValue) {
    return true;
  }

  if (!values) {
    return false;
  }

  return hasAnyValues(requiredValue, values);
};

var getProp = function getProp(props, propName) {
  return _get(props, propName);
};

export { hasAccess, hasAnyValues, hasBusinessLine, hasCapability, hasFeatureEnabled, hasPermission, hasValue };